import autobind from 'autobind-decorator';
import QueryString from 'query-string';
import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { AnyAction, Dispatch } from 'redux';

import { CompaniesSelectWithTheme } from 'common/components/companies-select-with-theme';
import { CrashDialog } from 'common/components/crash-dialog';
import { PageHeader } from 'common/components/page-header';
import { SubscriptionType } from 'common/constants/subscription';
import { RequestStatus } from 'common/enums/request-status';
import { State } from 'common/interfaces/state';
import { KreoDialogActions } from 'common/UIKit';
import { MetaTagUtils } from 'common/utils/meta-tag-utils';
import { Company } from 'unit-account/interfaces/company';
import { MIN_TEAM_SIZE } from 'unit-subscription/constants';
import { AppUrls } from '../../../../routes/app-urls';
import { SpinnerPage } from '../../../../routes/spinner-page';
import { SubscriptionActions } from '../../actions/creators';
import {
  SUBSCRIBE_PAGE_APPROVE_DIALOG,
  SubscribePageApproveDialog,
} from '../../components/subscribe-page-approve-dialog';
import { SubscriptionFaq } from '../../components/subscription-faq';
import { SubscriptionListing } from '../../components/subscription-listing';
import { BillingPeriodUnit } from '../../enums/billing-period-unit';
import { CreateSubscriptionForm } from '../../interfaces/create-subscription-form';
import { SubscriptionPlanListingModel } from '../../interfaces/subscription-plan-listing-model';
import { SubscriptionListingUtils } from '../../utils/subscription-listing-utils';
import { Styled } from './styled';

interface StateProps {
  subscriptionType: SubscriptionType;
  subscriptionPlansRequestStatus: RequestStatus;
  plansModel: SubscriptionPlanListingModel | null;
  selectedCompany: Company;
}

interface DispatchProps {
  getSubscriptionPlans: (subscriptionType: SubscriptionType) => void;
  openSubscriptionApproveDialog: () => void;
  createSubscription: (form: CreateSubscriptionForm) => void;
  dropSubscriptionPlansState: () => void;
}

interface PageState {
  selectedPlanGroup: string;
  billingPeriod: BillingPeriodUnit;
  selectedAddonGroups: string[];
}

interface PageProps extends StateProps, DispatchProps, RouteComponentProps<{}> { }

class SubscribePageComponent extends React.Component<PageProps, PageState> {
  constructor(props: PageProps) {
    super(props);

    const { period } = QueryString.parse(this.props.location.search);
    this.state = {
      billingPeriod: period in BillingPeriodUnit
        ? period
        : BillingPeriodUnit.Year,
      selectedAddonGroups: [],
      selectedPlanGroup: null,
    };
  }

  public componentDidMount(): void {
    this.getPlansIfNeeded();
    MetaTagUtils.setTitle('Select Plans');
  }

  public componentDidUpdate(prevProps: PageProps): void {
    this.setDefaultSelectedPlanIfNeeded(prevProps);
    this.setDefaultSelectedAddons(prevProps);
    this.getPlansIfNeeded();
  }

  public render(): React.ReactNode {
    if (!this.props.plansModel) {
      return <SpinnerPage />;
    }

    const { plansModel, subscriptionType } = this.props;
    const { billingPeriod, selectedAddonGroups } = this.state;
    const filteredPlanGroups = SubscriptionListingUtils.getFilteredPlanGroups(plansModel, billingPeriod);
    const filteredAddons = SubscriptionListingUtils.getFilteredAddons(plansModel, billingPeriod);
    const selectedAddons = SubscriptionListingUtils.getSelectedAddons(plansModel, billingPeriod, selectedAddonGroups);

    return (
      <Styled.Container>
        <Styled.Header>
          <PageHeader
            redirectUrl={AppUrls.products.path}
          >
            <CompaniesSelectWithTheme />
          </PageHeader>
        </Styled.Header>
        <Styled.Main>
          <SubscriptionListing
            billingPeriod={this.state.billingPeriod}
            filteredPlanGroups={filteredPlanGroups}
            filteredAddons={filteredAddons}
            selectedAddons={selectedAddons}
            selectedPlanGroup={this.state.selectedPlanGroup}
            selectedAddonGroups={this.state.selectedAddonGroups}
            onSubscriptionPlanGroupClick={this.onPlanGroupClick}
            onSelectedAddonGroupsChanged={this.onSelectedAddonGroupsChanged}
            onBillingPeriodChanged={this.setBillingPeriod}
          />
          <SubscriptionFaq />
        </Styled.Main>
        <SubscribePageApproveDialog
          subscriptionType={subscriptionType}
          onCreateSubscriptionClick={this.createSubscription}
          addons={filteredAddons}
          onBillingPeriodChanged={this.onBillingPeriodChanged}
          onSelectedAddonGroupsChanged={this.onSelectedAddonGroupsChanged}
          planVariant={filteredPlanGroups.find(x => x.name === this.state.selectedPlanGroup)}
          selectedAddonIds={selectedAddons.map(x => x.id)}
          billingPeriod={this.state.billingPeriod}
        />
        <CrashDialog />
      </Styled.Container>
    );
  }

  public componentWillUnmount(): void {
    this.props.dropSubscriptionPlansState();
  }

  @autobind
  private setDefaultSelectedPlanIfNeeded(prevProps: PageProps): void {
    const { plan } = QueryString.parse(this.props.location.search);
    if (!plan) {
      return;
    }

    if (prevProps.subscriptionPlansRequestStatus !== RequestStatus.Loaded
      && this.props.subscriptionPlansRequestStatus === RequestStatus.Loaded
    ) {
      const { plansModel } = this.props;
      const { billingPeriod } = this.state;
      const variant = SubscriptionListingUtils.getSelectedPlanVariant(plansModel, billingPeriod, plan);
      if (variant && variant.isSelfService) {
        this.setState({ selectedPlanGroup: plan });
        this.props.openSubscriptionApproveDialog();
      }
    }
  }

  @autobind
  private setDefaultSelectedAddons(prevProps: PageProps): void {
    if (prevProps.subscriptionPlansRequestStatus !== RequestStatus.Loaded
      && this.props.subscriptionPlansRequestStatus === RequestStatus.Loaded
    ) {
      const addons = SubscriptionListingUtils.getFilteredAddons(this.props.plansModel, this.state.billingPeriod);
      const defaultSelectedAddons = addons.map(addon => addon.isSelfService && addon.groupName);
      this.onSelectedAddonGroupsChanged(defaultSelectedAddons);
    }
  }

  private getPlansIfNeeded(): void {
    if (this.props.subscriptionType && this.props.subscriptionPlansRequestStatus === RequestStatus.NotRequested) {
      this.props.getSubscriptionPlans(this.props.subscriptionType);
    }
  }

  @autobind
  private onSelectedAddonGroupsChanged(selectedAddonGroups: string[]): void {
    this.setState({ selectedAddonGroups });
  }

  @autobind
  private onBillingPeriodChanged(billingPeriod: BillingPeriodUnit): void {
    this.setState({ billingPeriod });
  }

  @autobind
  private setBillingPeriod(billingPeriod: BillingPeriodUnit): void {
    this.setState({ billingPeriod });
  }

  @autobind
  private onPlanGroupClick(selectedPlanGroup: string): void {
    this.setState({ selectedPlanGroup });
    const { plansModel, selectedCompany, subscriptionType } = this.props;
    const { billingPeriod } = this.state;
    const variant = SubscriptionListingUtils.getSelectedPlanVariant(plansModel, billingPeriod, selectedPlanGroup);
    const companyActualSubscription = !!selectedCompany.subscriptions[subscriptionType];


    if (!!variant.trialPeriod && !companyActualSubscription) {
      this.startDefaultTrialSubscription(variant.id);
    } else {
      this.props.openSubscriptionApproveDialog();
    }
  }

  @autobind
  private createSubscription(
    quantity: number,
    paymentSourceId: string,
    billingCountry: string,
    vatNumber: string,
    coupons: string[],
  ): void {
    const { plansModel } = this.props;
    const { billingPeriod, selectedAddonGroups, selectedPlanGroup } = this.state;
    const selectedAddons = SubscriptionListingUtils.getSelectedAddons(plansModel, billingPeriod, selectedAddonGroups);
    const variant = SubscriptionListingUtils.getSelectedPlanVariant(plansModel, billingPeriod, selectedPlanGroup);

    this.props.createSubscription({
      addons: selectedAddons.map(x => x.id),
      paymentSourceId,
      planId: variant.id,
      quantity: variant.isCollaborationBlocked ? 1 : quantity,
      subscriptionType: this.props.subscriptionType,
      billingCountry,
      vatNumber,
      coupons,
    });
  }

  private startDefaultTrialSubscription(variantId: string): void {
    const { plansModel } = this.props;
    const { billingPeriod, selectedAddonGroups } = this.state;
    const selectedAddons = SubscriptionListingUtils.getSelectedAddons(plansModel, billingPeriod, selectedAddonGroups);
    const variant = SubscriptionListingUtils.getVariantByVariantId(plansModel, variantId);

    this.props.createSubscription({
      addons: selectedAddons.map(x => x.id),
      paymentSourceId: null,
      planId: variantId,
      quantity: variant.isCollaborationBlocked
        ? 1
        : variant?.minSeatCount || MIN_TEAM_SIZE,
      subscriptionType: this.props.subscriptionType,
      billingCountry: 'GB',
      vatNumber: null,
      coupons: [],
    });
  }
}

function mapStateToProps(state: State): StateProps {
  return {
    subscriptionType: state.account.selectedSubscriptionType,
    selectedCompany: state.account.selectedCompany,
    subscriptionPlansRequestStatus: state.account.requests.subscriptionPlans,
    plansModel: state.account.subscriptionPlans,
  };
}

function mapDispatchToProps(dispatch: Dispatch<AnyAction>): DispatchProps {
  return {
    createSubscription: form => dispatch(SubscriptionActions.createSubscription(form)),
    getSubscriptionPlans: type =>  dispatch(SubscriptionActions.getSubscriptionPlansRequest(type)),
    openSubscriptionApproveDialog: () => dispatch(KreoDialogActions.openDialog(SUBSCRIBE_PAGE_APPROVE_DIALOG)),
    dropSubscriptionPlansState: () =>  dispatch(SubscriptionActions.dropSubscriptionPlansState()),
  };
}

export const SubscribePage = connect(mapStateToProps, mapDispatchToProps)(SubscribePageComponent);
